<template>
  <template v-if="isMobile">
    <button
      type="button"
      :class="
        variant === 'pill'
          ? 'inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-full border border-gray-border bg-white px-4 py-2.5 text-left text-[15px] leading-[1.2] text-gray-dark transition-colors cursor-pointer active:border-orange'
          : 'flex min-w-0 w-full max-w-full items-center gap-1 text-left text-[14px] leading-[1.3] text-gray-dark transition-colors cursor-pointer lg:text-[15px] xl:gap-1.5 xl:text-[16px]'
      "
      @click="open = true"
    >
      <span class="shrink-0 whitespace-nowrap">{{ label }}</span>
      <span
        :title="currentLabel"
        :class="
          variant === 'pill'
            ? 'min-w-0 truncate font-helvetica-700 text-orange'
            : 'min-w-0 text-ellipsis-1 font-helvetica-700 text-orange'
        "
        >{{ currentLabel }}</span
      >
      <SvgoIconComponentFIconFilterarrow class="h-[7px] w-[10px] shrink-0 text-orange transition-transform duration-200" :class="open ? 'rotate-180' : ''"></SvgoIconComponentFIconFilterarrow>
    </button>

    <transition name="orders-sheet-fade">
      <div
        v-if="open"
        class="fixed inset-0 z-[1300] bg-black/30 lg:hidden"
        @click.self="open = false"
      >
        <transition name="orders-sheet-slide">
          <section
            v-if="open"
            class="absolute inset-x-0 bottom-0 rounded-t-[28px] bg-white px-5 pb-[calc(24px+env(safe-area-inset-bottom))] pt-5 shadow-[0_-12px_32px_rgb(0_0_0_/_0.12)]"
          >
            <div
              class="mx-auto flex w-full max-w-[var(--layout-mobile-max-width)] flex-col gap-4"
            >
              <div class="flex items-center justify-between gap-4">
                <p class="text-[24px] font-helvetica-700 text-gray-dark">
                  {{ label }}
                </p>
                <button
                  type="button"
                  class="relative flex h-10 w-10 items-center justify-center rounded-full border border-gray-border bg-white-gray text-gray-dark"
                  aria-label="Закрыть"
                  @click="open = false"
                >
                  <span
                    class="absolute h-[1.5px] w-4 rotate-45 rounded-full bg-current"
                  ></span>
                  <span
                    class="absolute h-[1.5px] w-4 -rotate-45 rounded-full bg-current"
                  ></span>
                </button>
              </div>

              <div class="space-y-3">
                <button
                  v-for="option in options"
                  :key="option.value"
                  type="button"
                  class="flex w-full items-center gap-3 rounded-[16px] border border-gray-border bg-white px-4 py-3 text-left text-[16px] leading-[22px] text-gray-dark transition-colors active:border-orange"
                  @click="select(option.value)"
                >
                  <span
                    class="h-5 w-5 rounded-full border bg-white"
                    :class="
                      modelValue === option.value
                        ? 'border-[5px] border-orange'
                        : 'border-gray-border'
                    "
                  />
                  <span class="truncate">{{ option.label }}</span>
                </button>
              </div>
            </div>
          </section>
        </transition>
      </div>
    </transition>
  </template>

  <UPopover
    v-else
    v-model:open="open"
    :content="{ side: 'bottom', align: 'start', sideOffset: 8 }"
    :ui="{ content: 'z-[1400]' }"
  >
    <button
      type="button"
      class="flex min-w-0 w-full max-w-full items-center gap-1 text-left text-[14px] leading-[1.3] text-gray-dark transition-colors cursor-pointer lg:text-[15px] xl:gap-1.5 xl:text-[16px]"
    >
      <span class="shrink-0 whitespace-nowrap">{{ label }}</span>
      <span
        :title="currentLabel"
        class="min-w-0 text-ellipsis-1 font-helvetica-700 text-orange"
        >{{ currentLabel }}</span
      >
      <SvgoIconComponentFIconFilterarrow class="h-[7px] w-[10px] shrink-0 text-orange transition-transform duration-200" :class="open ? 'rotate-180' : ''"></SvgoIconComponentFIconFilterarrow>
    </button>

    <template #content="{ close }">
      <div
        class="w-[min(340px,calc(100vw-32px))] rounded-[12px] border border-gray-border bg-white p-4 shadow-sm"
      >
        <div class="space-y-3">
          <button
            v-for="option in options"
            :key="option.value"
            type="button"
            class="flex w-full items-center gap-3 text-left text-[16px] leading-[22px] text-gray-dark transition-colors hover:text-orange cursor-pointer"
            @click="select(option.value, close)"
          >
            <span
              class="h-5 w-5 rounded-full border bg-white"
              :class="
                modelValue === option.value
                  ? 'border-[5px] border-orange'
                  : 'border-gray-border'
              "
            />
            <span class="min-w-0 whitespace-normal">{{ option.label }}</span>
          </button>
        </div>
      </div>
    </template>
  </UPopover>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";

type Option = { label: string; value: string };

const props = defineProps<{
  label: string;
  options: Option[];
  modelValue: string;
  variant?: "default" | "pill";
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
}>();

const open = ref(false);
const isMobile = ref(false);
let mediaQuery: MediaQueryList | null = null;
let syncViewport: (() => void) | null = null;

const currentLabel = computed(
  () =>
    props.options.find((o) => o.value === props.modelValue)?.label ??
    "Не выбрано",
);
const variant = computed(() => props.variant ?? "default");

const select = (value: string, close?: () => void) => {
  emit("update:modelValue", value);
  close?.();
  open.value = false;
};

onMounted(() => {
  if (!window.matchMedia) return;

  mediaQuery = window.matchMedia("(max-width: 1023px)");
  syncViewport = () => {
    isMobile.value = mediaQuery?.matches ?? false;
  };

  syncViewport();
  mediaQuery.addEventListener?.("change", syncViewport);
});

onBeforeUnmount(() => {
  if (!mediaQuery || !syncViewport) return;
  mediaQuery.removeEventListener?.("change", syncViewport);
});
</script>

<style scoped>
.orders-sheet-fade-enter-active,
.orders-sheet-fade-leave-active,
.orders-sheet-slide-enter-active,
.orders-sheet-slide-leave-active {
  transition: all 0.2s ease;
}

.orders-sheet-fade-enter-from,
.orders-sheet-fade-leave-to {
  opacity: 0;
}

.orders-sheet-slide-enter-from,
.orders-sheet-slide-leave-to {
  transform: translateY(18px);
  opacity: 0;
}
</style>
